import type { TFunction } from 'i18next';
import type { Delivery, IDeliveryLine, InventoryLevel } from '../interfaces/ILogistics.js';
import { fold } from '../utils/text.js';
import { unitLabel } from './units.js';

/**
 * The one place the frontend answers "which material on this truck?".
 *
 * An order carries 1..20 lines, and almost every surface that used to read
 * `delivery.itemName` is really asking about ONE material: the level being
 * restocked, the claim being booked, the Delivered stack being folded. Routing
 * all of them through these helpers is what keeps the fold (NFKC + lowercase,
 * the same one every delivery↔material link on this page uses) from being
 * re-implemented per surface — and drifting.
 */

/** The order's line for `materialName`, or null. A material appears at most
 *  once per order, so there is never a choice to make. */
export function lineFor(delivery: Delivery, materialName: string): IDeliveryLine | null {
  const key = fold(materialName);
  return delivery.lines.find((line) => fold(line.itemName) === key) ?? null;
}

/** An order and the line of it this surface is about — what every per-material
 *  lookup over a delivery LIST comes back as. */
export interface IOrderLine {
  order: Delivery;
  line: IDeliveryLine;
}

/** Every (order, line) pair in the list whose line is for `materialName`. */
export function orderLinesFor(deliveries: Delivery[], materialName: string): IOrderLine[] {
  const key = fold(materialName);
  return deliveries.flatMap((order) => {
    const line = order.lines.find((l) => fold(l.itemName) === key);
    return line === undefined ? [] : [{ order, line }];
  });
}

/**
 * The line a one-material surface means when it says "the delivery's material".
 *
 * `lines` is never empty by contract (the server refuses an order without one,
 * and the form refuses to remove the last row), but the array type cannot say
 * so — this is the single place that reads index 0, so the non-null assertion
 * lives here rather than at twenty call sites.
 */
export function firstLine(delivery: Delivery): IDeliveryLine {
  return delivery.lines[0]!;
}

/** True when the order carries exactly one material — the shape every surface
 *  that predates lines was designed for, and the one the card, the drawer
 *  title and the arrival merge still render specially. */
export function isSingleLine(delivery: Delivery): boolean {
  return delivery.lines.length === 1;
}

/** Every material on the truck, in line order — what a multi-material title,
 *  tooltip or search match reads. */
export function lineNames(delivery: Delivery): string[] {
  return delivery.lines.map((line) => line.itemName);
}

/** "2.5 t", "2.5" with no unit recorded, or null when the line records no
 *  amount at all (never 0 — an order with no amount is not an empty truck).
 *  Given `t`, a known unit reads in the viewer's language (`unitLabel`);
 *  without it, the stored text as is. */
export function lineAmount(line: IDeliveryLine, t?: (key: string) => string): string | null {
  if (line.quantity == null) return null;
  if (!line.unit) return String(line.quantity);
  return `${line.quantity} ${t ? unitLabel(t, line.unit) : line.unit}`;
}

/**
 * The site's OPEN line for a material: an order that has not landed, carrying
 * an unsynced line for it. The one-unsynced-line rule keeps at most one, so a
 * caller never has to choose; when the data says otherwise the first wins and
 * the backend's 409 is still the arbiter of any write.
 */
export function openLineFor(deliveries: Delivery[], materialName: string): IOrderLine | null {
  return (
    orderLinesFor(deliveries, materialName).find(
      ({ order, line }) => order.deliveredAt === null && !line.isSynced,
    ) ?? null
  );
}

/** The material a one-material surface is about, or undefined for a truck
 *  carrying several — there is then no single material to read a stock level,
 *  a lead time or an arrival merge against. */
export function soleMaterialOf(delivery: Delivery): string | undefined {
  return isSingleLine(delivery) ? firstLine(delivery).itemName : undefined;
}

/** How a delivery names itself in one line: "N material(s) · destination"
 *  (design 2026-09-14 §4.3, ruling 2026-09-15) — the same shape for one line
 *  as for several, like the board card; the material itself is the lines
 *  table's first row. */
export function deliveryTitle(delivery: Delivery, t: TFunction): string {
  return t('logistics.detail.materialsTitle', {
    count: delivery.lines.length,
    destination: delivery.destinationName,
  });
}

/**
 * How much of THIS material is on the incoming truck — the amount of the line
 * `level.incomingRef` names, never the truck's total (design 2026-09-14 §4.4:
 * "#DEL-902 · 2.5 t · ETA not set"). Null when the level has no incoming
 * order, when the page's cached list does not hold it yet, or when the line
 * recorded no amount.
 */
export function incomingAmountFor(deliveries: Delivery[], level: InventoryLevel): string | null {
  if (level.incomingRef === null) return null;
  const order = deliveries.find((d) => d.ref === level.incomingRef);
  if (order === undefined) return null;
  const line = lineFor(order, level.materialName);
  return line === null ? null : lineAmount(line);
}
